import { create } from "zustand";
import { get as idbGet, set as idbSet } from "idb-keyval";
import type { Template, TemplateContent } from "@/types/template";
import * as db from "@/store/db";
import { generateId } from "@/utils/id";
import { getSeedTemplates, getSeedHashes, isSeedId } from "@/store/seedTemplates";
import { parseTemplate } from "@/parser/templateParser";

const SEED_HASHES_KEY = "seed-hashes";

/** Sort templates by parsed name, parsing each source only once. */
function sortByName(templates: Template[]): void {
  const names = new Map(templates.map((t) => [t.id, parseTemplate(t.source).content.name]));
  templates.sort((a, b) => (names.get(a.id) ?? "").localeCompare(names.get(b.id) ?? ""));
}

interface TemplateStore {
  templates: Template[];
  loaded: boolean;
  load: () => Promise<void>;
  create: (name: string, isSubTemplate?: boolean) => Promise<Template>;
  cloneFromSeed: (seedId: string, editedSource: string) => Promise<Template>;
  save: (template: Template) => Promise<void>;
  remove: (id: string) => Promise<void>;
  getById: (id: string) => Template | undefined;
  getParsedContent: (id: string) => TemplateContent | undefined;
}

function replaceFrontmatterId(source: string, newId: string): string {
  const lines = source.split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (lines[i]?.startsWith("id:")) {
      lines[i] = `id: ${newId}`;
      break;
    }
  }
  return lines.join("\n");
}

async function createAndSave(
  editedSource: string,
  set: (fn: (s: { templates: Template[] }) => { templates: Template[] }) => void,
): Promise<Template> {
  const now = Date.now();
  const newId = generateId();
  const newSource = replaceFrontmatterId(editedSource, newId);
  const template: Template = { createdAt: now, id: newId, source: newSource, updatedAt: now };
  await db.saveTemplate(template);
  set((s) => {
    const updated = [...s.templates, template];
    sortByName(updated);
    return { templates: updated };
  });
  return template;
}

async function syncSeeds(templates: Template[]): Promise<void> {
  const storedHashes = (await idbGet<Record<string, number>>(SEED_HASHES_KEY)) ?? {};
  const currentHashes = getSeedHashes();
  const seeds = getSeedTemplates();
  for (const seed of seeds) {
    if (storedHashes[seed.id] !== currentHashes[seed.id]) {
      await db.saveTemplate(seed);
      const idx = templates.findIndex((t) => t.id === seed.id);
      if (idx === -1) {
        templates.push(seed);
      } else {
        templates[idx] = seed;
      }
    }
  }
  await idbSet(SEED_HASHES_KEY, currentHashes);
}

export const useTemplateStore = create<TemplateStore>((set, get) => ({
  cloneFromSeed: async (_seedId: string, editedSource: string) => createAndSave(editedSource, set),

  create: async (name: string, isSubTemplate = false) => {
    const safeName = name.replaceAll(/[\r\n]/g, " ").trim();
    const sub = isSubTemplate ? "sub-template: true\n" : "";
    const source = `---\nname: ${safeName}\nid: placeholder\n${sub}---\n\n# ${safeName}\n\n`;
    return createAndSave(source, set);
  },
  getById: (id: string) => get().templates.find((t) => t.id === id),

  getParsedContent: (id: string) => {
    const template = get().templates.find((t) => t.id === id);
    if (!template) {
      return;
    }
    const { content } = parseTemplate(template.source);
    return content;
  },

  load: async () => {
    const templates = await db.getAllTemplates();
    await syncSeeds(templates);
    sortByName(templates);
    set({ loaded: true, templates });
  },

  loaded: false,

  remove: async (id: string) => {
    if (isSeedId(id)) {
      return;
    }
    await db.deleteTemplate(id);
    set((s) => ({ templates: s.templates.filter((t) => t.id !== id) }));
  },

  save: async (template: Template) => {
    if (isSeedId(template.id)) {
      return;
    }
    const updated = { ...template, updatedAt: Date.now() };
    await db.saveTemplate(updated);
    set((s) => {
      const list = s.templates.map((t) => (t.id === updated.id ? updated : t));
      sortByName(list);
      return { templates: list };
    });
  },

  templates: [],
}));
